import { useChatSpaceMemory } from '../hooks/useChatSpaceMemory';
import { useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangleIcon, FolderIcon } from 'lucide-react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { AUDIO_TRANSCRIPTION_LIMITS } from '@shared/constants/audio-transcription';
import { AUDIO_ERROR_CODES } from '@shared/constants/error-codes';
import type { AudioAttachmentMime } from '@shared/schemas/audio-transcription';
import type { ChatRagScopeDocumentDTO, DocumentRevisionContextDTO } from '@shared/schemas/chat';
import { Button } from '@/components/ui/button';
import { SidebarTrigger } from '@/components/ui/sidebar';
import { cn } from '@/lib/utils';
import { apiBaseUrl, authClient } from '@/lib/auth-client';
import { useChatExperience } from '../chat-experience';
import { ChatExperienceProvider } from '../chat-experience-context';
import { ChatInput, type ComposerAttachment } from './ChatInput';
import { collectConversationDocuments } from '../collect-conversation-documents';
import { ChatConversationDocumentsPanel } from './ChatConversationDocumentsPanel';
import { ChatDocumentPanel } from './ChatDocumentPanel';
import { ChatOutlineLinkButton } from './ChatOutlineLinkButton';
import { ChatRagScopeBar } from './ChatRagScopeBar';
import { ChatSpaceNavigation } from './ChatSpaceSwitcher';
import { ChatWelcome } from './ChatWelcome';
import { AudioTranscriptionOnboardingDialog } from './AudioTranscriptionOnboardingDialog';
import { MessageList } from './MessageList';
import {
  buildNewChatLocationState,
  buildRouteComposerKey,
  CHAT_GENERAL_PATH,
  CHAT_RAG_PATH,
  extractChatConversationId,
  isChatNewRoute,
  parseChatLocationState,
  resolveChatSessionIntent,
  resolveNewChatWebSearchEnabled,
  type ChatLocationState,
} from '../chat-session-intent';
import {
  buildDocumentsLocationState,
  DOCUMENTS_PATH,
} from '../chat-space-navigation';
import { useConversation, useUpdateConversationWebSearchMode } from '../hooks/useConversation';
import {
  resolveChatRuntimeAudioTranscriptionEnabled,
  resolveChatRuntimeDictationEnabled,
  resolveChatRuntimeWebSearchDefault,
  useChatRuntimeDefaults,
} from '../hooks/useChatRuntimeDefaults';
import { ChatToolInteractionProvider } from '../chat-tool-interaction-context';
import { ChatMessageFeedbackProvider } from '../chat-message-feedback-context';
import { useChatStream } from '../hooks/useChatStream';
import { getChatErrorMessage } from '../chat-errors';
import { formatRejectedAttachmentMessages, getAttachmentUploadErrorMessage } from '../chat-attachment-feedback';
import { uploadChatAttachment } from '../services/chat-attachment.api';
import { deleteChatAttachment } from '../services/audio-transcription.api';
import {
  isAudioUploadAbortError,
  runWithConcurrency,
  uploadAudioFileDirect,
} from '../services/audio-direct-upload';
import { selectMixedChatAttachments } from '../chat-attachment-selection';
import {
  markAudioTranscriptionOnboardingSeen,
  hasSeenAudioTranscriptionOnboarding,
  resolveAudioTranscriptionQuickAction,
  shouldShowAudioTranscriptionQuickAction,
} from '../audio-transcription-quick-action';
import type { ChatUIMessage, ChatUIFilePart } from '../types';
import { hasPendingComposerAttachments, isChatComposerLocked } from '../chat-composer-state';
import { toChatUiMessages } from '../types';
import { getQuotaBlockedTooltip, QuotaStatusBanner, useQuotaStatus } from '@/features/quota';
import { useOrganizationAccess } from '@/features/organization/useOrganizationAccess';

function createComposerAttachmentId() {
  return `composer-${crypto.randomUUID()}`;
}

type AttachmentUploadSession = {
  abort: () => void;
  attachmentId?: string;
  kind: 'audio' | 'document';
};

function getReadyAttachments(attachments: ComposerAttachment[]) {
  return attachments.filter((attachment) => attachment.status === 'ready' && attachment.attachmentId);
}

function getMessageText(message: ChatUIMessage) {
  return message.parts
    .filter((part): part is Extract<ChatUIMessage['parts'][number], { type: 'text' }> => part.type === 'text')
    .map((part) => part.text)
    .join('\n')
    .trim();
}

function getMessageAttachments(message: ChatUIMessage): ComposerAttachment[] {
  return getMessageFileParts(message).map((part) => ({
    attachmentId: part.attachmentId,
    durationMs: part.durationMs ?? null,
    filename: part.filename,
    id: part.attachmentId ?? part.url ?? createComposerAttachmentId(),
    kind: part.kind,
    mediaType: part.mediaType,
    sizeBytes: part.sizeBytes,
    status: 'ready' as const,
    url: part.url,
  }));
}

function getMessageFileParts(message: ChatUIMessage): ChatUIFilePart[] {
  return message.parts
    .filter((part) => part.type === 'file')
    .map((part) => part as unknown as ChatUIFilePart);
}

function ChatPageContent() {
  const params = useParams<{ conversationId?: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const sessionState = authClient.useSession();
  const conversationId = params.conversationId ?? extractChatConversationId(location.pathname);
  const lastHandledNewChatKeyRef = useRef<string | null>(null);
  const lastHandledNewChatPathRef = useRef<string | null>(null);
  // « Premier rendu » à ignorer uniquement quand on démarre à froid sur une route
  // « nouveau chat » (rien à réinitialiser). Si l'instance démarre sur une
  // conversation, toute navigation ultérieure vers /chat est un vrai « nouveau
  // chat » et doit réinitialiser dès le premier clic (sinon il en faut deux).
  const isFirstNewChatEffectRef = useRef(!conversationId);
  const { newChatKey, ragScope: locationRagScope } = parseChatLocationState(location.state);
  const conversationQuery = useConversation(conversationId);
  const initialMessages = useMemo(
    () => (conversationQuery.data ? toChatUiMessages(conversationQuery.data) : []),
    [conversationQuery.data],
  );
  const {
    activeDocument,
    activeSourceReference,
    activeSourceReferences,
    clearDocument,
    closeDocument,
    isDocumentOpen,
    openDocument,
    patchDocument,
    setActiveSourceReference,
  } = useChatExperience();
  const serverConversationRagEnabled = conversationQuery.data?.conversation?.ragEnabled;
  const serverConversationWebSearchEnabled = conversationQuery.data?.conversation?.webSearchEnabled ?? false;
  const chatIntent = resolveChatSessionIntent({
    conversationId,
    conversationRagEnabled: serverConversationRagEnabled,
    pathname: location.pathname,
  });
  const chatRuntimeDefaultsQuery = useChatRuntimeDefaults(true);
  const newChatWebSearchDefaultEnabled = resolveChatRuntimeWebSearchDefault(
    chatRuntimeDefaultsQuery.data,
  );
  const audioTranscriptionEnabled = resolveChatRuntimeAudioTranscriptionEnabled(
    chatRuntimeDefaultsQuery.data,
  );
  const dictationEnabled = resolveChatRuntimeDictationEnabled(chatRuntimeDefaultsQuery.data);
  const routeComposerKey = buildRouteComposerKey({
    chatIntent,
    conversationId,
    newChatKey,
  });
  const [isConversationDocumentsOpen, setIsConversationDocumentsOpen] = useState(false);
  const [composerKey, setComposerKey] = useState(routeComposerKey);
  const [input, setInput] = useState('');
  const [attachments, setAttachments] = useState<ComposerAttachment[]>([]);
  // In-flight upload promises keyed by composer id, so a fast submit can wait
  // for them to settle before building the outgoing message.
  const pendingAttachmentUploadsRef = useRef<Map<string, Promise<void>>>(new Map());
  // Settled upload outcomes keyed by composer id (the resolved attachment, or
  // null if the upload failed / was removed). Written outside React state so the
  // send flow reads fresh results right after awaiting, regardless of commit timing.
  const attachmentUploadResultsRef = useRef<Map<string, ComposerAttachment | null>>(new Map());
  const attachmentUploadSessionsRef = useRef<Map<string, AttachmentUploadSession>>(new Map());
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isOnboardingOpen, setIsOnboardingOpen] = useState(false);
  // Transient "flushing" state: the user submitted while uploads were still
  // running, so we queue the send and wait for the files before dispatching.
  const [isFlushingAttachments, setIsFlushingAttachments] = useState(false);
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [documentRevisionContext, setDocumentRevisionContext] = useState<
    (DocumentRevisionContextDTO & { title: string; version: number }) | null
  >(null);
  const [newChatWebSearchOverride, setNewChatWebSearchOverride] = useState<{
    enabled: boolean;
    routeComposerKey: string;
  } | null>(null);
  const [conversationWebSearchOverrides, setConversationWebSearchOverrides] = useState<Record<string, boolean>>({});
  // Same ownership as the web search mode: a new chat owns its scope by composer
  // key, a conversation by id until the server copy (sent with the next turn) wins.
  const [newChatRagScopeOverride, setNewChatRagScopeOverride] = useState<{
    documents: ChatRagScopeDocumentDTO[];
    routeComposerKey: string;
  } | null>(null);
  const [conversationRagScopeOverrides, setConversationRagScopeOverrides] = useState<
    Record<string, ChatRagScopeDocumentDTO[]>
  >({});
  const ragEnabled = conversationId
    ? (serverConversationRagEnabled ?? chatIntent === 'document-search')
    : chatIntent === 'document-search';
  const webSearchEnabled = conversationId
    ? (conversationWebSearchOverrides[conversationId] ?? serverConversationWebSearchEnabled)
    : (
      newChatWebSearchOverride?.routeComposerKey === routeComposerKey
        ? newChatWebSearchOverride.enabled
        : resolveNewChatWebSearchEnabled(chatIntent, newChatWebSearchDefaultEnabled)
    );
  const conversationRagScopeOverride = conversationId ? conversationRagScopeOverrides[conversationId] : undefined;
  const ragScope = useMemo<ChatRagScopeDocumentDTO[]>(() => {
    if (!ragEnabled) return [];
    if (conversationId) {
      return conversationRagScopeOverride ?? conversationQuery.data?.conversation?.ragScope ?? [];
    }
    return newChatRagScopeOverride?.routeComposerKey === routeComposerKey
      ? newChatRagScopeOverride.documents
      : (locationRagScope ?? []);
  }, [
    conversationId,
    conversationQuery.data?.conversation?.ragScope,
    conversationRagScopeOverride,
    locationRagScope,
    newChatRagScopeOverride,
    ragEnabled,
    routeComposerKey,
  ]);
  // An existing conversation keeps its persisted scope unless the user changed it here.
  const ragDocumentIds = useMemo(() => {
    if (!ragEnabled) return undefined;
    if (conversationId) return conversationRagScopeOverride?.map((document) => document.id);
    return ragScope.length > 0 ? ragScope.map((document) => document.id) : undefined;
  }, [conversationId, conversationRagScopeOverride, ragEnabled, ragScope]);
  const isComposerCurrent = composerKey === routeComposerKey;
  const currentInput = isComposerCurrent ? input : '';
  const currentAttachments = isComposerCurrent ? attachments : [];
  const currentEditingMessageId = isComposerCurrent ? editingMessageId : null;
  const updateConversationWebSearchMode = useUpdateConversationWebSearchMode();
  const quotaQuery = useQuotaStatus();
  const organizationAccess = useOrganizationAccess();
  const chat = useChatStream({
    conversationId,
    initialMessages,
    initialMessagesReady: !conversationId || conversationQuery.isSuccess,
    newChatKey,
    ownerId: sessionState.data?.user.id,
    quotaBlocked: quotaQuery.blocked,
    ragDocumentIds,
    ragEnabled,
    webSearchEnabled,
    onDocumentDelta: ({ delta, documentId }) => {
      patchDocument(documentId, (current) => {
        const nextContent = `${current.content}${delta}`;

        return {
          ...current,
          content: nextContent,
          status: 'streaming',
          summary: nextContent.slice(0, 220),
        };
      });
    },
    onDocumentFinish: (document) => {
      patchDocument(document.documentId, document);
    },
    onQuotaExceeded: () => {
      void quotaQuery.refetchImmediately();
    },
    onStreamSettled: (outcome) => {
      if (outcome.type === 'success' || outcome.type === 'aborted') {
        void quotaQuery.refetchAfterStream();
      }
    },
  });
  const hasMessages = chat.messages.length > 0;
  const isSidePanelOpen = isDocumentOpen || isConversationDocumentsOpen;
  const conversationDocuments = useMemo(
    () => collectConversationDocuments(chat.messages, apiBaseUrl),
    [chat.messages],
  );
  const isSending = chat.status === 'submitted' || chat.status === 'streaming';
  const isQuotaComposerBlocked = quotaQuery.blocked && !chat.hasPendingAskUser;
  const isComposerLocked = isChatComposerLocked({
    isAskUserSubmitting: chat.isAskUserSubmitting,
    quotaBlocked: isQuotaComposerBlocked,
    status: chat.status,
  })
    // While we wait for queued uploads to settle, lock the composer to prevent a
    // second submit from racing the first one.
    || isFlushingAttachments;
  const quotaBlockedTooltip = isQuotaComposerBlocked
    ? getQuotaBlockedTooltip(quotaQuery.status)
    : undefined;
  const resetChat = chat.reset;
  const stageHorizontalPadding = 'px-5 md:px-6';
  const toolInteractionValue = useMemo(() => ({
    beginDocumentRevision: setDocumentRevisionContext,
    isAskUserSubmitting: chat.isAskUserSubmitting,
    retryAskUserContinuation: chat.retryAskUserContinuation,
    submitAskUserOutput: chat.submitAskUserOutput,
    toolContinuationError: chat.toolContinuationError,
  }), [
    setDocumentRevisionContext,
    chat.isAskUserSubmitting,
    chat.retryAskUserContinuation,
    chat.submitAskUserOutput,
    chat.toolContinuationError,
  ]);

  useEffect(() => {
    const uploadSessions = attachmentUploadSessionsRef.current;
    return () => {
      for (const session of uploadSessions.values()) {
        session.abort();
      }
      uploadSessions.clear();
    };
  }, []);

  useEffect(() => {
    // Reset piloté par le changement de conversation, couplé à l'action store
    // clearDocument() : effet légitime (synchronisation à la route), pas une
    // dérivation de state — d'où la désactivation ciblée de la règle.
    clearDocument();
    setIsConversationDocumentsOpen(false);
    setDocumentRevisionContext(null);
  }, [clearDocument, conversationId]);

  const getSpaceEntryPath = useChatSpaceMemory(sessionState.data?.user.id, location.pathname, conversationId, conversationQuery.error);

  useEffect(() => {
    if (conversationId || location.pathname !== CHAT_GENERAL_PATH) {
      return;
    }

    const state = location.state as ChatLocationState | null;

    if (state?.chatIntent === 'document-search') {
      navigate(CHAT_RAG_PATH, {
        replace: true,
        state: buildNewChatLocationState(),
      });
    }
  }, [conversationId, location.pathname, location.state, navigate]);

  useEffect(() => {
    if (conversationId || !isChatNewRoute(location.pathname)) {
      return;
    }

    if (isFirstNewChatEffectRef.current) {
      isFirstNewChatEffectRef.current = false;
      lastHandledNewChatPathRef.current = location.pathname;
      if (newChatKey) {
        lastHandledNewChatKeyRef.current = newChatKey;
      }
      return;
    }

    const shouldResetForKey = Boolean(newChatKey && lastHandledNewChatKeyRef.current !== newChatKey);
    const shouldResetForPath = lastHandledNewChatPathRef.current !== location.pathname;

    if (!shouldResetForKey && !shouldResetForPath) {
      return;
    }

    if (newChatKey) {
      lastHandledNewChatKeyRef.current = newChatKey;
    }
    lastHandledNewChatPathRef.current = location.pathname;
    // Reset « nouveau chat » piloté par la navigation, couplé à resetChat()/clearDocument()
    // (actions store) : effet légitime, pas une dérivation de state.
    setNewChatWebSearchOverride(null);
    resetChat();
    clearDocument();
  }, [clearDocument, conversationId, location.pathname, newChatKey, resetChat]);

  function replaceComposerState(nextState: {
    attachments: ComposerAttachment[];
    editingMessageId: string | null;
    input: string;
  }) {
    setComposerKey(routeComposerKey);
    setInput(nextState.input);
    setAttachments(nextState.attachments);
    setEditingMessageId(nextState.editingMessageId);
  }

  async function handleFilesSelected(files: FileList | File[]) {
    if (isComposerLocked) {
      return;
    }

    const existingAudioCount = currentAttachments.filter((attachment) => attachment.kind === 'audio').length;
    const existingDocumentCount = currentAttachments.filter((attachment) => attachment.kind !== 'audio').length;
    const { accepted, rejected } = selectMixedChatAttachments({
      audioEnabled: audioTranscriptionEnabled,
      existingAudioCount,
      existingDocumentCount,
      files: Array.from(files),
    });

    for (const message of formatRejectedAttachmentMessages(rejected)) {
      toast.error(message);
    }

    if (accepted.length === 0) {
      return;
    }

    setComposerKey(routeComposerKey);

    const pendingItems = accepted.map((item) => ({
      ...item,
      composerId: createComposerAttachmentId(),
    }));

    setAttachments((current) => [
      ...current,
      ...pendingItems.map((item) => ({
        filename: item.file.name,
        id: item.composerId,
        kind: item.kind,
        mediaType: item.mediaType,
        progress: 0,
        sizeBytes: item.file.size,
        status: 'uploading' as const,
      })),
    ]);

    await runWithConcurrency(
      pendingItems,
      AUDIO_TRANSCRIPTION_LIMITS.maxConcurrentUploads,
      async (item) => {
        const abortController = new AbortController();
        attachmentUploadSessionsRef.current.set(item.composerId, {
          abort: () => abortController.abort(),
          kind: item.kind,
        });

        const uploadPromise = (async () => {
          try {
            if (item.kind === 'audio') {
              const uploaded = await uploadAudioFileDirect({
                conversationId,
                file: item.file,
                mediaType: item.mediaType as AudioAttachmentMime,
                onPrepared: (attachmentId) => {
                  const session = attachmentUploadSessionsRef.current.get(item.composerId);
                  if (session) {
                    session.attachmentId = attachmentId;
                  }
                },
                onProgress: (percent) => {
                  setAttachments((current) => current.map((attachment) => (
                    attachment.id === item.composerId
                      ? { ...attachment, progress: percent, status: 'uploading' }
                      : attachment
                  )));
                },
                onStatusChange: (status) => {
                  setAttachments((current) => current.map((attachment) => (
                    attachment.id === item.composerId
                      ? { ...attachment, status }
                      : attachment
                  )));
                },
                signal: abortController.signal,
              });

              if (uploaded.uploadStatus !== 'ready') {
                const failed: ComposerAttachment = {
                  attachmentId: uploaded.attachmentId,
                  durationMs: uploaded.durationMs,
                  errorCode: uploaded.errorCode,
                  filename: uploaded.filename,
                  id: item.composerId,
                  kind: 'audio',
                  mediaType: uploaded.mediaType,
                  sizeBytes: uploaded.sizeBytes,
                  status: 'failed',
                };
                attachmentUploadResultsRef.current.set(item.composerId, null);
                setAttachments((current) => current.map((attachment) => (
                  attachment.id === item.composerId ? failed : attachment
                )));
                toast.error(getChatErrorMessage({
                  code: uploaded.errorCode ?? AUDIO_ERROR_CODES.AUDIO_UPLOAD_VALIDATION_FAILED,
                }));
                return;
              }

              const resolved: ComposerAttachment = {
                attachmentId: uploaded.attachmentId,
                durationMs: uploaded.durationMs,
                filename: uploaded.filename,
                id: item.composerId,
                kind: 'audio',
                mediaType: uploaded.mediaType,
                progress: 100,
                sizeBytes: uploaded.sizeBytes,
                status: 'ready',
              };
              attachmentUploadResultsRef.current.set(item.composerId, resolved);
              setAttachments((current) => current.map((attachment) => (
                attachment.id === item.composerId ? resolved : attachment
              )));
              return;
            }

            const uploaded = await uploadChatAttachment(item.file, conversationId);
            const resolved: ComposerAttachment = {
              attachmentId: uploaded.attachmentId,
              filename: uploaded.filename,
              id: item.composerId,
              kind: 'document',
              mediaType: uploaded.mediaType,
              sizeBytes: uploaded.sizeBytes,
              status: uploaded.extractionStatus === 'ready' ? 'ready' : 'failed',
            };
            attachmentUploadResultsRef.current.set(item.composerId, resolved.status === 'ready' ? resolved : null);
            setAttachments((current) => current.map((attachment) => (
              attachment.id === item.composerId ? resolved : attachment
            )));
          } catch (error) {
            if (isAudioUploadAbortError(error) || abortController.signal.aborted) {
              attachmentUploadResultsRef.current.set(item.composerId, null);
              setAttachments((current) => current.filter((attachment) => attachment.id !== item.composerId));
              return;
            }

            attachmentUploadResultsRef.current.set(item.composerId, null);
            if (item.kind === 'audio') {
              setAttachments((current) => current.map((attachment) => (
                attachment.id === item.composerId
                  ? { ...attachment, status: 'failed' as const }
                  : attachment
              )));
            } else {
              setAttachments((current) => current.filter((attachment) => attachment.id !== item.composerId));
            }
            toast.error(getAttachmentUploadErrorMessage(error, item.file.name));
          } finally {
            pendingAttachmentUploadsRef.current.delete(item.composerId);
            attachmentUploadSessionsRef.current.delete(item.composerId);
          }
        })();

        pendingAttachmentUploadsRef.current.set(item.composerId, uploadPromise);
        await uploadPromise;
      },
    );
  }

  async function handleSendMessage(nextInput = currentInput, nextAttachments = currentAttachments) {
    if (isComposerLocked) {
      return;
    }

    const trimmedInput = nextInput.trim();

    // Nothing to send at all (no text and not even a queued file).
    if (!trimmedInput && nextAttachments.length === 0) {
      return;
    }

    if (updateConversationWebSearchMode.isPending) {
      toast.error('Veuillez patienter le temps que la recherche web soit synchronisée.');
      return;
    }

    // File d'attente : si des uploads sont encore en vol, on attend leur fin
    // avant de construire le message — sinon le fichier serait silencieusement
    // exclu et perdu (race "soumission rapide après sélection").
    let attachmentsToSend = nextAttachments;
    if (hasPendingComposerAttachments(nextAttachments)) {
      setIsFlushingAttachments(true);
      try {
        await Promise.allSettled([...pendingAttachmentUploadsRef.current.values()]);
      } finally {
        setIsFlushingAttachments(false);
      }
      // Rebuild from the snapshot using the settled outcomes (resolved attachment
      // or null when the upload failed/was removed), independent of render timing.
      attachmentsToSend = nextAttachments.flatMap((attachment) => {
        const settled = attachmentUploadResultsRef.current.get(attachment.id);
        if (settled === undefined) {
          return [attachment];
        }
        return settled === null ? [] : [settled];
      });
    }

    const readyAttachments = getReadyAttachments(attachmentsToSend);
    const notSentCount = nextAttachments.length - readyAttachments.length;

    // Plus rien d'exploitable après attente : ni texte, ni fichier prêt.
    if (!trimmedInput && readyAttachments.length === 0) {
      if (notSentCount > 0) {
        toast.error('Le fichier n’a pas pu être joint. Réessayez.');
      }
      return;
    }

    if (notSentCount > 0) {
      toast.error('Certains fichiers n’ont pas pu être joints et ont été ignorés.');
    }

    const previousInput = nextInput;
    const previousAttachments = attachmentsToSend;
    const previousEditingMessageId = currentEditingMessageId;

    setComposerKey(routeComposerKey);
    setInput('');
    setAttachments([]);
    attachmentUploadResultsRef.current.clear();
    setEditingMessageId(null);
    setDocumentRevisionContext(null);

    try {
      await chat.sendMessage({
        parts: [
          ...readyAttachments.map((attachment) => ({
            attachmentId: attachment.attachmentId!,
            durationMs: attachment.durationMs,
            filename: attachment.filename,
            kind: attachment.kind,
            mediaType: attachment.mediaType,
            sizeBytes: attachment.sizeBytes,
            type: 'file' as const,
            url: `attachment://${attachment.attachmentId}`,
          })),
          ...(trimmedInput ? [{
            text: trimmedInput,
            type: 'text' as const,
          }] : []),
        ] as ChatUIMessage['parts'],
        role: 'user',
      }, documentRevisionContext ? {
        body: {
          documentRevisionContext: {
            baseGenerationId: documentRevisionContext.baseGenerationId,
            generatedDocumentId: documentRevisionContext.generatedDocumentId,
          },
        },
      } : undefined);
    } catch (error) {
      setInput(previousInput);
      setAttachments(previousAttachments);
      setEditingMessageId(previousEditingMessageId);
      setDocumentRevisionContext(documentRevisionContext);
      // Pas de re-throw : personne ne catch en amont (rejet non géré silencieux),
      // le feedback utilisateur passe par le toast.
      toast.error(getChatErrorMessage(error));
    }
  }

  function handleRemoveAttachment(attachmentId: string) {
    const session = attachmentUploadSessionsRef.current.get(attachmentId);
    session?.abort();
    const preparedAttachmentId = session?.attachmentId
      ?? currentAttachments.find((attachment) => attachment.id === attachmentId)?.attachmentId;
    if (preparedAttachmentId && (session?.kind === 'audio' || currentAttachments.find((attachment) => attachment.id === attachmentId)?.kind === 'audio')) {
      void deleteChatAttachment(preparedAttachmentId);
    }
    attachmentUploadSessionsRef.current.delete(attachmentId);
    setComposerKey(routeComposerKey);
    setAttachments((current) => current.filter((attachment) => attachment.id !== attachmentId));
    attachmentUploadResultsRef.current.delete(attachmentId);
    pendingAttachmentUploadsRef.current.delete(attachmentId);
  }

  function handleEditMessage(message: ChatUIMessage) {
    chat.clearTurnError();
    replaceComposerState({
      attachments: getMessageAttachments(message),
      editingMessageId: message.id,
      input: getMessageText(message),
    });
  }

  function handleRetryAssistant() {
    if (isComposerLocked) {
      return;
    }

    void chat.regenerate();
  }

  function handleRetryTurn() {
    void chat.retryTurn();
  }

  async function handleWebSearchChange(nextValue: boolean) {
    if (nextValue === webSearchEnabled) {
      return;
    }

    if (isSending) {
      toast.error('Veuillez attendre la fin de la génération en cours avant de modifier la recherche web.');
      return;
    }
    if (!conversationId) {
      setNewChatWebSearchOverride({
        enabled: nextValue,
        routeComposerKey,
      });
      return;
    }

    setConversationWebSearchOverrides((current) => ({
      ...current,
      [conversationId]: nextValue,
    }));

    try {
      await updateConversationWebSearchMode.mutateAsync({
        conversationId,
        webSearchEnabled: nextValue,
      });
    } catch (error) {
      setConversationWebSearchOverrides((current) => ({
        ...current,
        [conversationId]: !nextValue,
      }));
      toast.error(getChatErrorMessage(error));
    }
  }

  function handleRagScopeChange(documents: ChatRagScopeDocumentDTO[]) {
    if (conversationId) {
      setConversationRagScopeOverrides((current) => ({ ...current, [conversationId]: documents }));
      return;
    }
    setNewChatRagScopeOverride({ documents, routeComposerKey });
  }

  const ragScopeBar = (
    <ChatRagScopeBar disabled={isSending} documents={ragScope} onChange={handleRagScopeChange} />
  );

  const composerWebSearchProps = ragEnabled || (!conversationId && chatIntent === 'general')
    ? {
        isWebSearchToggleDisabled: updateConversationWebSearchMode.isPending || isSending || isComposerLocked,
        onWebSearchChange: (enabled: boolean) => void handleWebSearchChange(enabled),
        showWebSearchToggle: true as const,
        webSearchEnabled,
      }
    : {};

  const showAudioTranscriptionAction = shouldShowAudioTranscriptionQuickAction({
    conversationId,
    enabled: audioTranscriptionEnabled,
    hasMessages,
    intent: chatIntent,
  });

  function focusComposerInput() {
    document.querySelector<HTMLTextAreaElement>('[data-testid="chat-composer-input"]')?.focus();
  }

  function handleTranscribeAudioClick() {
    const result = resolveAudioTranscriptionQuickAction({
      currentInput: currentInput,
      hasSeenOnboarding: hasSeenAudioTranscriptionOnboarding(sessionState.data?.user.id),
    });
    setComposerKey(routeComposerKey);
    setInput(result.nextInput);

    if (result.type === 'open-onboarding') {
      setIsOnboardingOpen(true);
      return;
    }

    fileInputRef.current?.click();
  }

  function handleOnboardingChooseFiles() {
    markAudioTranscriptionOnboardingSeen(sessionState.data?.user.id);
    setIsOnboardingOpen(false);
    fileInputRef.current?.click();
  }

  function handleOnboardingEditPrompt() {
    markAudioTranscriptionOnboardingSeen(sessionState.data?.user.id);
    setIsOnboardingOpen(false);
    focusComposerInput();
  }

  function handleOpenConversationDocuments() {
    closeDocument();
    setIsConversationDocumentsOpen(true);
  }

  function handleOpenConversationDocument(document: Parameters<typeof openDocument>[0]) {
    setIsConversationDocumentsOpen(false);
    openDocument(document);
  }

  return (
    <ChatToolInteractionProvider value={toolInteractionValue}>
    <ChatMessageFeedbackProvider conversationId={conversationId}>
    <section className="flex h-dvh w-full flex-row overflow-hidden">
      {conversationQuery.isError ? (
        <section className="flex flex-1 items-center justify-center px-6">
          <div className="w-full max-w-3xl rounded-[var(--radius-surface-3xl)] border border-destructive/30 bg-destructive/5 p-5 text-sm text-destructive">
            <div className="flex items-center gap-2">
              <AlertTriangleIcon className="size-4" />
              {getChatErrorMessage(conversationQuery.error)}
            </div>
          </div>
        </section>
      ) : (
        <>
        <div
          className={cn(
            'flex min-w-0 flex-col bg-background transition-[width] duration-300 ease-[cubic-bezier(0.32,0.72,0,1)]',
            isDocumentOpen && 'w-[48%]',
            isConversationDocumentsOpen && !isDocumentOpen && 'min-w-0 flex-1',
            !isSidePanelOpen && 'w-full',
          )}
        >
          <header
            className={`relative sticky top-0 z-20 flex h-[3.25rem] w-full min-w-0 shrink-0 items-center bg-background/92 ${stageHorizontalPadding} backdrop-blur`}
          >
            <div className="pointer-events-none absolute start-1/2 top-1/2 z-10 flex -translate-x-1/2 -translate-y-1/2 flex-col items-center rtl:translate-x-1/2">
              <div className="pointer-events-auto">
                <ChatSpaceNavigation value={chatIntent} entryPath={getSpaceEntryPath} />
              </div>
            </div>

            <div className="grid w-full min-w-0 grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-2">
              <div className="flex min-w-0 justify-start">
                <SidebarTrigger className="-ml-1 text-foreground md:hidden" />
              </div>

              <div className="min-w-0" aria-hidden />

              <div className="flex min-w-0 justify-end">
                {hasMessages ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    className="rounded-full text-muted-foreground hover:bg-muted/70 hover:text-foreground"
                    aria-label="Voir les documents de la conversation"
                    title="Documents de la conversation"
                    data-testid="conversation-documents-trigger"
                    onClick={handleOpenConversationDocuments}
                  >
                    <FolderIcon className="size-[1.125rem] stroke-[1.75]" />
                  </Button>
                ) : null}
              </div>
            </div>
          </header>

          <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden bg-background">
            <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
              {hasMessages ? (
                <>
                  <MessageList
                    isAskUserSubmitting={chat.isAskUserSubmitting}
                    isDocumentSearch={ragEnabled}
                    isSending={isSending}
                    messages={chat.messages}
                    onEditMessage={handleEditMessage}
                    onRetryAssistant={handleRetryAssistant}
                    onRetryTurn={handleRetryTurn}
                    status={chat.status}
                    turnError={chat.turnError}
                  />

                  <div className={`relative shrink-0 bg-background ${stageHorizontalPadding} pb-6 pt-2 before:pointer-events-none before:absolute before:inset-x-0 before:-top-10 before:z-10 before:h-10 before:bg-gradient-to-t before:from-background before:to-transparent`}>
                    <div className="mx-auto flex w-full max-w-[48rem] flex-col gap-3">
                      <QuotaStatusBanner
                        canManageCompany={organizationAccess.canReadCompanyUsage}
                        status={quotaQuery.status}
                      />
                      {ragScopeBar}
                      <ChatInput
	                        attachments={currentAttachments}
                          audioTranscriptionEnabled={audioTranscriptionEnabled}
                          dictationEnabled={dictationEnabled}
                          dictationScopeKey={routeComposerKey}
                          documentRevisionContext={documentRevisionContext ?? undefined}
                          fileInputRef={fileInputRef}
	                        input={currentInput}
	                        isComposerLocked={isComposerLocked}
                          isStopRequested={chat.isStopRequested}
	                        lockedReason={quotaBlockedTooltip}
	                        status={chat.status}
	                        onCancelEditing={currentEditingMessageId ? () => replaceComposerState({ attachments: [], editingMessageId: null, input: '' }) : undefined}
	                        onChangeInput={(value) => {
	                          setComposerKey(routeComposerKey);
	                          setInput(value);
	                        }}
                          onCancelDocumentRevision={() => setDocumentRevisionContext(null)}
	                        onFilesSelected={handleFilesSelected}
	                        onRemoveAttachment={handleRemoveAttachment}
                        onSendMessage={() => handleSendMessage()}
                        onStop={chat.stop}
                        {...composerWebSearchProps}
                      />

                    </div>
                  </div>
                </>
              ) : conversationQuery.isLoading ? (
                <div className="flex flex-1 items-center justify-center px-6 text-sm text-muted-foreground">
                  Chargement de la conversation...
                </div>
              ) : (
                <main className={`flex min-h-0 flex-1 flex-col overflow-visible ${stageHorizontalPadding}`}>
                  <div className="mx-auto flex w-full max-w-[52rem] flex-1 flex-col overflow-visible">
                    <div className="flex shrink basis-auto flex-col justify-end max-sm:grow max-sm:justify-center sm:min-h-[calc(42svh-3.25rem)]">
                      <ChatWelcome
                        intent={chatIntent}
                        onTranscribeAudioClick={handleTranscribeAudioClick}
                        showAudioTranscriptionAction={showAudioTranscriptionAction}
                      />
                    </div>

                    <div className="mx-auto w-full max-w-[48rem] shrink-0 overflow-visible pb-8 pt-12 md:pt-10">
                      <div className="mb-3">
                        <QuotaStatusBanner
                          canManageCompany={organizationAccess.canReadCompanyUsage}
                          status={quotaQuery.status}
                        />
                      </div>
                      {ragScopeBar}
                      <ChatInput
	                        attachments={currentAttachments}
                          audioTranscriptionEnabled={audioTranscriptionEnabled}
                          dictationEnabled={dictationEnabled}
                          dictationScopeKey={routeComposerKey}
                          documentRevisionContext={documentRevisionContext ?? undefined}
                          fileInputRef={fileInputRef}
	                        input={currentInput}
	                        isComposerLocked={isComposerLocked}
                          isStopRequested={chat.isStopRequested}
	                        layout="centered"
	                        lockedReason={quotaBlockedTooltip}
	                        status={chat.status}
	                        onCancelEditing={currentEditingMessageId ? () => replaceComposerState({ attachments: [], editingMessageId: null, input: '' }) : undefined}
	                        onChangeInput={(value) => {
	                          setComposerKey(routeComposerKey);
	                          setInput(value);
	                        }}
                          onCancelDocumentRevision={() => setDocumentRevisionContext(null)}
	                        onFilesSelected={handleFilesSelected}
	                        onRemoveAttachment={handleRemoveAttachment}
                        onSendMessage={() => handleSendMessage()}
                        onStop={chat.stop}
                        {...composerWebSearchProps}
                      />

                      {ragEnabled && !conversationId ? (
                        <div className="mt-4 flex justify-center">
                          <ChatOutlineLinkButton
                            className="h-9 gap-2 rounded-full px-4"
                            data-testid="rag-manage-documents-button"
                            icon={FolderIcon}
                            title="Gérer la base documentaire"
                            to={DOCUMENTS_PATH}
                            state={buildDocumentsLocationState(location.pathname)}
                          >
                            Gérer la base documentaire
                          </ChatOutlineLinkButton>
                        </div>
                      ) : null}
                    </div>
                  </div>
                </main>
              )}
            </div>
          </div>
        </div>

        <ChatConversationDocumentsPanel
          conversationId={conversationId}
          documents={conversationDocuments}
          isOpen={isConversationDocumentsOpen}
          onOpenChange={setIsConversationDocumentsOpen}
          onOpenDocument={handleOpenConversationDocument}
        />
        <ChatDocumentPanel
          activeSourceReference={activeSourceReference}
          activeSourceReferences={activeSourceReferences}
          conversationId={conversationId}
          document={activeDocument}
          onSelectSourceReference={setActiveSourceReference}
          isOpen={isDocumentOpen && !isConversationDocumentsOpen}
          onOpenChange={(open) => {
            if (!open) {
              closeDocument();
            }
          }}
        />
        <AudioTranscriptionOnboardingDialog
          onChooseFiles={handleOnboardingChooseFiles}
          onEditPrompt={handleOnboardingEditPrompt}
          onOpenChange={setIsOnboardingOpen}
          open={isOnboardingOpen}
        />
        </>
      )}
    </section>
    </ChatMessageFeedbackProvider>
    </ChatToolInteractionProvider>
  );
}

export function ChatPage() {
  return (
    <ChatExperienceProvider>
      <ChatPageContent />
    </ChatExperienceProvider>
  );
}
